/* Tech Notes 
  Parent owns the source of truth
  Child keeps its own internal working copy
  Child only commits to parent when the row is valid..This means the child needs a temporary working state.
  parent must use UseMem.

  Add/Edit is done through a modal (owned entirely by this component). The inline table is
  always read-only; "Add new+" opens the modal blank, the row-level edit icon opens the modal
  pre-filled. The modal commits a validated row into local state - the section's own
  Edit/Cancel/Save buttons still control when the whole list is pushed up to the parent
  (same updateFlow/editMode/readonly semantics as Travel/Meal).
*/
import { GoabButton, GoabButtonGroup, GoabDrawer, GoabFormItem, GoabIcon, GoabIconButton, GoabModal, GoabRadioGroup, GoabRadioItem } from "@abgov/react-components";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, createFilterOptions, TextField, Tooltip } from "@mui/material";
import { useAuth } from "react-oidc-context";

import "./WorkInfo.scss"
import { GenListType, RelationElement, WorkClaimsExpenses } from "../../../../../common/types";
import { wdpUtil } from "../../../../../common/utils/util";
import { appLog } from "../../../../../common/utils/app-log";
import { AppContext } from "../../../../../common/contexts/apiContext";
import ApiService from "../../../../../sevices/ApiService";
import { domianTableLookupIdAssignedToWildfire, domianTableLookupIdCostingActivity, domianTableLookupIdCostRateType, domianTableLookupIdFuelProvidedByGoa, domianTableLookupIdWorkPerformed } from "../../../../../common/constants";

export interface WorkProps {
  updateFlow: boolean;
  canShowEdit?: boolean;
  values: WorkClaimsExpenses[];
  referenceDate?: string | null;
  activeSection?: string | null;
  isBusy?: boolean;
  onChange: (changeType:string,change:WorkClaimsExpenses|WorkClaimsExpenses[]|boolean|null ) => void;
}

const filterOptionsGenListType = createFilterOptions({ matchFrom: 'any', stringify: (option: GenListType) => option?.label });

export function WorkInfo(props: WorkProps) {

  const myAppCtx = useContext(AppContext);
  const auth = useAuth();

  const [secStatus, setSecStatus]   = useState({editMode: !props.updateFlow});
  const [deleteModalState, setDeleteModalState] = useState<boolean>(false);

  // form Control state managment
  type WorkRowState = {values: WorkClaimsExpenses;errors: WorkClaimsExpenses;touched: WorkClaimsExpenses};
  const emptyState: WorkClaimsExpenses = {id: "",startUtc: "",finishUtc: "",noOfUnits: "",workPerformed: null,fireNumber: null,assignedToWildfire: "",fuelProvidedByGoa: ""};
  const loadedTouchedState: WorkClaimsExpenses = {id: "",startUtc: "T",finishUtc: "T",noOfUnits: "",workPerformed: null,fireNumber: null,assignedToWildfire: "",fuelProvidedByGoa: ""};

  // INTERNAL STATE INITIALIZE (no more auto-created blank row - "Add new+" opens the modal instead)
  const [formCtrl, setFormCtrl] = useState<WorkRowState[]>(() =>
    props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }}))
  );

  /***************************************************************************************
   * . SYNC INTERNAL STATE WHEN PROPS CHANGE
  ****************************************************************************************/
  useEffect(() => {
    setFormCtrl(() => props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }})));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values]);

  useEffect(() => {
    setSecStatus({ editMode: !props.updateFlow });
  }, [props.updateFlow]);

  // Enforce single-active-section restriction: if another section becomes the active
  // one being edited, auto-cancel this section's own in-progress edit.
  useEffect(() => {
    if (props.activeSection !== undefined && props.activeSection !== 'work' && secStatus.editMode && props.updateFlow) {
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeSection]);

  //*********************************************************************************
  // Check if section is dirty (different from original props) 
  //*********************************************************************************/
  function isDirty(): boolean {
    if (formCtrl.length !== props.values.length) {
      return true;
    }
    return formCtrl.some((rowState, index) => {
      const originalRow = props.values[index];
      if (!originalRow) return true;
      return (
        rowState.values.startUtc  !== originalRow.startUtc  ||
        rowState.values.finishUtc !== originalRow.finishUtc ||
        wdpUtil.getFormFieldVal(rowState.values.workPerformed as GenListType | string | null, true) !== wdpUtil.getFormFieldVal(originalRow.workPerformed as GenListType | string | null, true) ||
        wdpUtil.getFormFieldVal(rowState.values.fireNumber as GenListType | string | null, true)     !== wdpUtil.getFormFieldVal(originalRow.fireNumber as GenListType | string | null, true) ||
        (rowState.values.assignedToWildfire || "") !== (originalRow.assignedToWildfire || "") ||
        (rowState.values.fuelProvidedByGoa  || "") !== (originalRow.fuelProvidedByGoa  || "")
      );
    });
  }
  const isSectionDirty = isDirty();
  const isSaveEnabled = isSectionDirty;
  //#######################################################
  function getReferenceDate(): Dayjs {
    const parsed = dayjs(props.referenceDate);
    return parsed.isValid() ? parsed : dayjs();
  }

  function parseTimeValue(value: string): Dayjs | null {
    const trimmed = String(value || "").trim();
    if (!trimmed) return null;

    const asIso = dayjs(trimmed);
    if (asIso.isValid()) return asIso;

    const hhmm = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(trimmed);
    if (!hhmm) return null;

    const ref = getReferenceDate();
    return ref.hour(Number(hhmm[1])).minute(Number(hhmm[2])).second(0).millisecond(0);
  }

  function toReferenceDateTime(value: string, isFinishTime?: boolean, startValue?: string): Dayjs | null {
    const parsed = parseTimeValue(value);
    if (!parsed || !parsed.isValid()) return null;
    const ref = getReferenceDate();
    let anchored = ref.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
    
    // Detect overnight work: if this is finish time and it's strictly earlier than start time, add 1 day.
    // NOTE: equal times must NOT be bumped to next day, otherwise a 09:00 -> 09:00 entry would
    // incorrectly calculate as a full 24-hour total instead of being flagged as an invalid (0 hour) entry.
    if (isFinishTime && startValue) {
      const startParsed = parseTimeValue(startValue);
      if (startParsed && startParsed.isValid()) {
        const startAnchored = ref.hour(startParsed.hour()).minute(startParsed.minute()).second(0).millisecond(0);
        // If finish time is before start time (on clock), assume next day
        if (anchored.isBefore(startAnchored)) {
          anchored = anchored.add(1, 'day');
        }
      }
    }
    
    return anchored;
  }

  function recalcNoOfUnits(row: WorkRowState) {
    const start = toReferenceDateTime(String(row.values.startUtc || ""));
    const finish = toReferenceDateTime(String(row.values.finishUtc || ""), true, String(row.values.startUtc || ""));
    if (!start || !finish) {
      row.values.noOfUnits = 0;
      return;
    }
    const diff = finish.diff(start, "hour", true);
    row.values.noOfUnits = diff > 0 ? Number(diff.toFixed(2)) : 0;
  }

  function formatTimeOnly(value: string | number | null | undefined): string {
    if (value === null || value === undefined || String(value).trim() === "") {
      return "";
    }

    const parsed = parseTimeValue(String(value));
    return parsed ? parsed.format("HH:mm") : String(value);
  }

  // Displays decimal hours (e.g. 3.33) as a clock duration "H:MM" (e.g. "3:20"),
  // matching the 24h HH:mm format used for the start/stop time fields.
  function formatHoursAsDuration(value: string | number | null | undefined): string {
    const hours = typeof value === "number" ? value : parseFloat(String(value ?? ""));
    if (!Number.isFinite(hours) || hours <= 0) {
      return "";
    }
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${String(m).padStart(2, "0")}`;
  }

  /***************************************************************************************
  ****************************************************************************************/
  function validateField(who: keyof WorkClaimsExpenses,value: any,row: WorkClaimsExpenses): string {

    // 1. Time fields validation
    if (who === "startUtc" || who === "finishUtc") {
      // 1a. Required check
      if (!String(value || "").trim()) {
        return who === "startUtc"
          ? "Select a start time."
          : "Select a stop time.";
      }

      // 1b. Invalid time format
      const isFinish = who === "finishUtc";
      const startVal = isFinish ? String(row.startUtc || "") : undefined;
      const val = toReferenceDateTime(String(value), isFinish, startVal);

      if (!val) {
        return "Invalid time format.";
      }

      // 1c. Stop time must be later than start time (only validate if both times exist)
      if (who === "finishUtc" && row.startUtc) {
        const startTime = toReferenceDateTime(String(row.startUtc), false, undefined);
        const finishTime = toReferenceDateTime(String(value), true, String(row.startUtc));
        
        if (startTime && finishTime && !finishTime.isAfter(startTime)) {
          return "Stop time must be later than start time.";
        }
      }
    }

    // 2. Total hours validation (noOfUnits)
    if (who === "noOfUnits") {
      const hours = typeof value === "number" ? value : parseFloat(String(value || "0"));
      if (hours <= 0) {
        return "Total hours must be greater than 0.";
      }
      if (hours > 24) {
        return "Total hours cannot exceed 24 hours.";
      }
    }

    // 3. Fire number - required when the user was assigned to a wildfire
    if (who === "fireNumber") {
      if (row.assignedToWildfire === "yes" && wdpUtil.isNullOrEmpty(value as GenListType | string | null)) {
        return "Select a fire number.";
      }
    }

    // 4. Yes/No questions - required when they are actually asked for the selected work performed
    if (who === "assignedToWildfire" || who === "fuelProvidedByGoa") {
      const flags = workPerformedQuestionFlags.get((row.workPerformed as GenListType | null)?.label ?? "");
      const isShown = who === "assignedToWildfire" ? flags?.showAssignedToWildfire : flags?.showFuelProvidedByGoa;
      if (isShown && !String(value || "").trim()) {
        return "Select Yes or No.";
      }
    }

    return "";
  }

  /***************************************************************************************
  // Row delete
  ****************************************************************************************/
  function onDeleteRow(index: number) {
    setFormCtrl(prev => prev.filter((_, i) => i !== index));
  }
  /***************************************************************************************
  // OnEdit mode (section-level)
  ****************************************************************************************/
  function onEdit(){
    props.onChange("editStart", true);
    setSecStatus({editMode:!secStatus.editMode});
  }
  /***************************************************************************************
  // OnCancel (section-level)
  ****************************************************************************************/
  function onCancel(){
    restoreFromProps();
    setSecStatus({editMode: !props.updateFlow});
  }

  function restoreFromProps() {
    setFormCtrl(props.values.map(v => ({values: v,errors: { ...emptyState },touched: { ...loadedTouchedState }})));
  }

  /***************************************************************************************
  // OnSave - confirmation gate only
  ****************************************************************************************/
  function onSave(){
    const originalIds = new Set(props.values.map(row => String(row.id || "")));
    const currentDraftIds = new Set(formCtrl.map(row => String(row.values.id || "")));
    const hasDeletionImpact = Array.from(originalIds).some(id => !currentDraftIds.has(id));

    if (hasDeletionImpact) {
      setDeleteModalState(true);
      return;
    }

    doSave();
  }

  /***************************************************************************************
  // doSave - rows are already validated/normalized when committed through the modal
  ****************************************************************************************/
  function doSave(){
    const validRows = formCtrl.map(row => row.values);
    props.onChange("save", validRows);

    // In create flow there is no Edit button, so keep section editable after save.
    setSecStatus({editMode: !props.updateFlow});
  }

  function onCancelDeleteModal() {
    setDeleteModalState(false);
    restoreFromProps();
  }

  function onConfirmDeleteModal() {
    setDeleteModalState(false);
    doSave();
  }

  /***************************************************************************************
  ****************************************************************************************/
  function isRowValid(row: WorkRowState): boolean {
    return (
      row.errors.startUtc === ""  && row.touched.startUtc === "T" &&
      row.errors.finishUtc === "" && row.touched.finishUtc === "T"
    );
  }

  /* ################################################################################## *
   * Add / Edit modal - fully owned/managed by this component
   * ################################################################################## */
  const [modalState, setModalState] = useState<{open: boolean;mode: "add" | "edit";rowIndex: number | null;draft: WorkRowState}>({
    open: false,
    mode: "add",
    rowIndex: null,
    draft: {values: { ...emptyState, id: "" },errors: { ...emptyState },touched: { ...emptyState }},
  });

  const [fireNumberOptions, setFireNumberOptions] = useState<GenListType[]>([]);
  const fireNumberAbortRef = useRef<AbortController | null>(null);

  // Controlled input text for the "Work performed" Autocomplete (same controlled inputValue/onInputChange
  // pattern used by forestAreaId/unitNumber/equipment/addOns in TicketDetails.tsx) so typing works reliably
  // while `value` is also controlled.
  const [workPerformedInputValue, setWorkPerformedInputValue] = useState('');

  // "Work performed" dropdown data - extracted from the raw WORK_PERFORMED lookup-relationships/key
  // response (kept unflattened in AppContext since it bundles several lookupTypeIds per item).
  // IMPORTANT: FIRES' WORK_PERFORMED catalog can define TWO separate lookupId entries that share the exact
  // same label (e.g. "Equipment Being Hauled" / "Fuel Cache Restocking") - one combo set for Assigned To
  // Wildfire = Yes, another for = No - each mapping to a different activity code. Grouping by lookupId (as
  // before) left both showing up as visually-identical duplicate rows with no way to tell them apart. We
  // now group everything by LABEL so each label appears once in the dropdown, and treat Assigned/Fuel as
  // real questions whenever they select between different underlying lookupIds/activity codes for that
  // label - see resolveWorkPerformedId() below for how the concrete lookupId actually gets persisted.
  const workPerformedCatalog = useMemo(() => {
    const relations = myAppCtx?.lookups?.workPerformedRelationsList ?? [];

    const toYesNo = (label: string | undefined | null): "yes" | "no" | "" => {
      const normalized = String(label || "").trim().toLowerCase();
      return normalized === "yes" ? "yes" : normalized === "no" ? "no" : "";
    };

    type ComboRecord = { lookupId: string; assigned: "yes" | "no" | ""; fuel: "yes" | "no" | ""; outcome: string };
    const combosByLabel = new Map<string, ComboRecord[]>();

    relations.forEach(rel => {
      (rel.lookupRelationshipItems ?? []).forEach(item => {
        const elements = item.itemElements ?? [];
        const workPerformedElement = elements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdWorkPerformed));
        if (!workPerformedElement) return;

        const label = (workPerformedElement.lookupLabel ?? "").trim();
        if (!label) return;

        const assignedElement = elements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdAssignedToWildfire));
        const fuelElement = elements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdFuelProvidedByGoa));
        const rateTypeElement = elements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdCostRateType));
        const costingActivityElement = elements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdCostingActivity));

        const combos = combosByLabel.get(label) ?? [];
        combos.push({
          lookupId: (workPerformedElement.lookupId ?? "").toString(),
          assigned: toYesNo(assignedElement?.lookupLabel),
          fuel: toYesNo(fuelElement?.lookupLabel),
          // Rate Type + Costing Activity lookupIds, plus the raw activityCode carried directly on the Work
          // Performed element itself - some combos only differ by this (no separate Costing Activity element).
          outcome: `${rateTypeElement?.lookupId ?? ""}|${costingActivityElement?.lookupId ?? ""}|${workPerformedElement.activityCode ?? ""}`,
        });
        combosByLabel.set(label, combos);
      });
    });

    // Does varying `getField` (holding `getOtherField` fixed) ever change the outcome across the given combos?
    const fieldAffectsOutcome = (combos: ComboRecord[], getField: (c: ComboRecord) => string, getOtherField: (c: ComboRecord) => string): boolean => {
      const groupsByOther = new Map<string, Map<string, Set<string>>>();
      combos.forEach(c => {
        const fieldVal = getField(c);
        if (!fieldVal) return;
        const otherVal = getOtherField(c);
        const outcomesByField = groupsByOther.get(otherVal) ?? new Map<string, Set<string>>();
        const outcomes = outcomesByField.get(fieldVal) ?? new Set<string>();
        outcomes.add(c.outcome);
        outcomesByField.set(fieldVal, outcomes);
        groupsByOther.set(otherVal, outcomesByField);
      });

      for (const outcomesByField of groupsByOther.values()) {
        if (outcomesByField.size <= 1) continue;
        const outcomeSignatures = new Set(Array.from(outcomesByField.values()).map(s => Array.from(s).sort().join(",")));
        if (outcomeSignatures.size > 1) return true;
      }
      return false;
    };

    const flagsByLabel = new Map<string, { showAssignedToWildfire: boolean; assignedDefaultValue: "yes" | "no" | ""; showFuelProvidedByGoa: boolean; fuelDefaultValue: "yes" | "no" | "" }>();

    combosByLabel.forEach((combos, label) => {
      const distinctAssigned = new Set(combos.map(c => c.assigned).filter(Boolean));
      const distinctFuel = new Set(combos.map(c => c.fuel).filter(Boolean));

      const assignedMatters = distinctAssigned.size > 1 && fieldAffectsOutcome(combos, c => c.assigned, c => c.fuel);
      const fuelMatters = distinctFuel.size > 1 && fieldAffectsOutcome(combos, c => c.fuel, c => c.assigned);

      flagsByLabel.set(label, {
        showAssignedToWildfire: assignedMatters,
        assignedDefaultValue: distinctAssigned.size === 1 ? (Array.from(distinctAssigned)[0] as "yes" | "no") : "",
        showFuelProvidedByGoa: fuelMatters,
        fuelDefaultValue: distinctFuel.size === 1 ? (Array.from(distinctFuel)[0] as "yes" | "no") : "",
      });
    });

    const options: GenListType[] = Array.from(combosByLabel.entries())
      .map(([label, combos]) => ({ label, value: combos[0].lookupId }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { options, combosByLabel, flagsByLabel };
  }, [myAppCtx?.lookups?.workPerformedRelationsList]);

  const workPerformedOptions = workPerformedCatalog.options;
  const workPerformedQuestionFlags = workPerformedCatalog.flagsByLabel;

  // Given the chosen label + the user's final Assigned/Fuel answers, resolve the concrete underlying
  // lookupId to persist - needed because a single label can map to different lookupIds (and activity
  // codes) depending on those answers (see workPerformedCatalog comment above).
  function resolveWorkPerformedId(label: string, assignedToWildfire: string, fuelProvidedByGoa: string): string {
    const combos = workPerformedCatalog.combosByLabel.get(label) ?? [];
    if (combos.length === 0) return "";
    const assigned = assignedToWildfire === "yes" || assignedToWildfire === "no" ? assignedToWildfire : "";
    const fuel = fuelProvidedByGoa === "yes" || fuelProvidedByGoa === "no" ? fuelProvidedByGoa : "";

    const exact = combos.find(c => (!assigned || c.assigned === assigned) && (!fuel || c.fuel === fuel));
    return (exact ?? combos[0]).lookupId;
  }

  function blankDraftRow(): WorkRowState {
    return {
      values: { id: wdpUtil.getUniqueId(),startUtc: "",finishUtc: "",noOfUnits: 0,workPerformed: null,fireNumber: null,assignedToWildfire: "",fuelProvidedByGoa: "" },
      errors: { ...emptyState },
      touched: { ...emptyState },
    };
  }

  function openAddModal() {
    setFireNumberOptions([]);
    setWorkPerformedInputValue('');
    setModalState({ open: true, mode: "add", rowIndex: null, draft: blankDraftRow() });
  }

  function openEditModal(index: number) {
    const row = formCtrl[index];
    if (!row) return;
    setFireNumberOptions((row.values.fireNumber ? [row.values.fireNumber as GenListType] : []));
    setWorkPerformedInputValue((row.values.workPerformed as GenListType | null)?.label ?? '');
    setModalState({
      open: true,
      mode: "edit",
      rowIndex: index,
      draft: { values: { ...row.values },errors: { ...emptyState },touched: { ...loadedTouchedState } },
    });
  }

  function closeModal() {
    setModalState(prev => ({ ...prev, open: false }));
  }

  function onMyChange(patch: Partial<WorkClaimsExpenses>) {
    setModalState(prev => {
      const values = { ...prev.draft.values, ...patch };
      // Re-evaluate live (rather than only on save) so a previously shown error clears
      // as soon as the user selects a fire number or answers a Yes/No question.
      const errors = {
        ...prev.draft.errors,
        fireNumber: validateField("fireNumber", values.fireNumber, values),
        assignedToWildfire: validateField("assignedToWildfire", values.assignedToWildfire, values),
        fuelProvidedByGoa: validateField("fuelProvidedByGoa", values.fuelProvidedByGoa, values),
      };
      return { ...prev, draft: { ...prev.draft, values, errors } };
    });
  }

  // When the "Work performed" selection changes, either clear answers to questions that don't apply
  // to the newly selected item at all, or silently auto-apply the single fixed answer when the item's
  // combo records don't actually offer a real choice (see workPerformedQuestionFlags above).
  function onWorkPerformedChange(item: GenListType | null) {
    const flags = workPerformedQuestionFlags.get(item?.label ?? "");
    const patch: Partial<WorkClaimsExpenses> = { workPerformed: item };

    if (flags?.showAssignedToWildfire) {
      patch.assignedToWildfire = "";
      patch.fireNumber = null;
    } else {
      patch.assignedToWildfire = flags?.assignedDefaultValue || "";
      patch.fireNumber = null;
    }
    patch.fuelProvidedByGoa = flags?.showFuelProvidedByGoa ? "" : (flags?.fuelDefaultValue || "");

    onMyChange(patch);
  }

  function onWorkPerformedInputChange(newInputValue: string, reason: string) {
    setWorkPerformedInputValue(newInputValue);
  }

  function searchFireNumbers(qStr: string) {
    const trimmed = qStr.trim();

    // Do not search until 2+ chars (same threshold as the Cost Summary table)
    if (trimmed.length < 2) {
      setFireNumberOptions([]);
      return;
    }

    // Abort any in-flight request before issuing a new one (same pattern as loadFires in TicketDetails.tsx)
    fireNumberAbortRef.current?.abort();
    const controller = new AbortController();
    fireNumberAbortRef.current = controller;

    ApiService.queryFires(auth.user?.access_token as string, trimmed, controller).then(res => {
      const list = res?.data;
      if (!list || list.length === 0) {
        setFireNumberOptions([]);
        return;
      }
      const goodList: GenListType[] = list.map((elem: any) => ({ label: `${elem.fireNumber} - ${elem.year}`, value: elem.fireNumber }));
      setFireNumberOptions(goodList);
    }).catch(error => {
      if (error?.name === "CanceledError") return;
      appLog.debug(error);
    });
  }

  function onModalTimeFieldBlur(who: "startUtc" | "finishUtc", rawValue: string) {
    const committed = wdpUtil.normalizeTypedTimeInput(rawValue);
    setModalState(prev => {
      const row: WorkRowState = { values: { ...prev.draft.values, [who]: committed },errors: { ...prev.draft.errors },touched: { ...prev.draft.touched, [who]: "T" } };

      const startErr  = validateField("startUtc", row.values.startUtc,  row.values);
      const finishErr = validateField("finishUtc", row.values.finishUtc, row.values);
      row.errors.startUtc  = startErr;
      row.errors.finishUtc = finishErr;

      if (startErr === "" && finishErr === "") {
        recalcNoOfUnits(row);
      } else {
        row.values.noOfUnits = 0;
      }

      return { ...prev, draft: row };
    });
  }

  function onModalSave(keepOpenForAnother?: boolean) {
    setModalState(prev => {
      const row: WorkRowState = { values: { ...prev.draft.values },errors: { ...prev.draft.errors },touched: { ...prev.draft.touched, startUtc: "T", finishUtc: "T", fireNumber: "T", assignedToWildfire: "T", fuelProvidedByGoa: "T" } };

      row.errors.startUtc  = validateField("startUtc", row.values.startUtc, row.values);
      row.errors.finishUtc = validateField("finishUtc", row.values.finishUtc, row.values);

      if (row.errors.startUtc === "" && row.errors.finishUtc === "") {
        recalcNoOfUnits(row);
        row.errors.noOfUnits = validateField("noOfUnits", row.values.noOfUnits, row.values);
      }

      row.errors.fireNumber = validateField("fireNumber", row.values.fireNumber, row.values);
      row.errors.assignedToWildfire = validateField("assignedToWildfire", row.values.assignedToWildfire, row.values);
      row.errors.fuelProvidedByGoa  = validateField("fuelProvidedByGoa", row.values.fuelProvidedByGoa, row.values);

      const hasErrors = row.errors.startUtc !== "" || row.errors.finishUtc !== "" || row.errors.noOfUnits !== "" || row.errors.fireNumber !== "" || row.errors.assignedToWildfire !== "" || row.errors.fuelProvidedByGoa !== "";
      if (hasErrors || !isRowValid(row)) {
        return { ...prev, draft: row };
      }

      // Clear fire number if the user answered "No" to being assigned to a wildfire.
      if (row.values.assignedToWildfire !== "yes") {
        row.values.fireNumber = null;
      }

      // Resolve the concrete lookupId for the chosen label + answers (a label can map to more than one
      // underlying lookupId/activity code - see workPerformedCatalog comment).
      const workPerformedLabel = (row.values.workPerformed as GenListType | null)?.label ?? "";
      if (workPerformedLabel) {
        const resolvedId = resolveWorkPerformedId(workPerformedLabel, String(row.values.assignedToWildfire || ""), String(row.values.fuelProvidedByGoa || ""));
        if (resolvedId) {
          row.values.workPerformed = { ...(row.values.workPerformed as GenListType), value: resolvedId };
        }
      }

      const normalizedStart  = toReferenceDateTime(String(row.values.startUtc || ""));
      const normalizedFinish = toReferenceDateTime(String(row.values.finishUtc || ""), true, String(row.values.startUtc || ""));
      const finalValues: WorkClaimsExpenses = {
        ...row.values,
        startUtc: normalizedStart ? normalizedStart.toISOString() : String(row.values.startUtc || ""),
        finishUtc: normalizedFinish ? normalizedFinish.toISOString() : String(row.values.finishUtc || ""),
      };

      setFormCtrl(prevRows => {
        if (prev.mode === "add") {
          return [...prevRows, { values: finalValues,errors: { ...emptyState },touched: { ...loadedTouchedState } }];
        }
        const updated = [...prevRows];
        updated[prev.rowIndex as number] = { values: finalValues,errors: { ...emptyState },touched: { ...loadedTouchedState } };
        return updated;
      });

      // "Add another" keeps the modal open in add mode with a fresh blank draft instead of closing.
      if (keepOpenForAnother && prev.mode === "add") {
        setFireNumberOptions([]);
        setWorkPerformedInputValue('');
        return { ...prev, open: true, draft: blankDraftRow() };
      }

      return { ...prev, open: false };
    });
  }

  const selectedWorkPerformedQuestionFlags = workPerformedQuestionFlags.get((modalState.draft.values.workPerformed as GenListType | null)?.label ?? "");
  const showAssignedToWildfire = selectedWorkPerformedQuestionFlags?.showAssignedToWildfire ?? false;
  const showFuelProvidedByGoa  = selectedWorkPerformedQuestionFlags?.showFuelProvidedByGoa ?? false;

  /* ################################################################################## */
  // mian tsx
  /* ################################################################################## */
  return (
    <section className="work-section">
      
      <div className="header-and-actions">
        {formCtrl.length === 0 && <p>There is no records yet.</p>}
        <div className="header-actions">
          {props.updateFlow   && (props.canShowEdit ?? false) && !secStatus.editMode && <GoabButton type='text' size='compact' disabled={props.isBusy ?? false} onClick={() => onEdit()}>Edit</GoabButton>}
          {props.updateFlow && secStatus.editMode && ( <>
                                    <GoabButton type='text' size='compact' onClick={() => onCancel()}>Cancel</GoabButton>
                                    <GoabButton type='secondary' size='compact' onClick={() => onSave()} disabled={!isSaveEnabled || (props.isBusy ?? false)}>Save</GoabButton>
                                    </>
                                  )
          }
        </div>
      </div>
      <div className="work-cards">
          {formCtrl.map((rowState, i) => {
            const timeRange = [formatTimeOnly(rowState.values.startUtc as string), formatTimeOnly(rowState.values.finishUtc as string)].filter(Boolean).join("-");
            const fireNumberLabel = wdpUtil.getFormFieldVal(rowState.values.fireNumber as GenListType | string | null, true);
            const fuelProvidedByGoa = rowState.values.fuelProvidedByGoa;
            const hours = rowState.values.noOfUnits ? formatHoursAsDuration(rowState.values.noOfUnits) : "";

            return (
              <div className="work-card" key={`${i}-${rowState.values.id}`}>
                <div className="work-card-content">
                  <div className="work-card-header">
                    <span className="work-card-title">{i + 1}. {wdpUtil.getFormFieldVal(rowState.values.workPerformed as GenListType | string | null, true)}</span>
                  </div>
                  <div className="work-card-meta">
                    {timeRange && <span className="meta-item"><GoabIcon type="time" size="small" /> {timeRange}</span>}
                    {fireNumberLabel && <span className="meta-item"><GoabIcon type="pricetag" size="small" /> {fireNumberLabel}</span>}
                    {fuelProvidedByGoa === "yes" && <span className="meta-item"><GoabIcon type="water" size="small" /> Fuel provided by GoA</span>}
                    {fuelProvidedByGoa === "no" && <span className="meta-item"><GoabIcon type="water" size="small" /> Fuel not provided by GoA</span>}
                    {hours && <span className="meta-item work-card-hours">Hours: <b>{hours}</b></span>}
                  </div>
                </div>
                {secStatus.editMode && props.updateFlow && <span className="row-actions-wrap">
                    <GoabIconButton testId={"tic-work-sec-th-editBtn" + i} icon='pencil' size='medium' onClick={() => openEditModal(i)} />
                    <GoabIconButton testId={"tic-work-sec-th-trashBtn" + i} icon='trash' size='medium' onClick={() => onDeleteRow(i)} />
                  </span>
                }
              </div>
            );
          })}
          {secStatus.editMode && props.updateFlow && <div style={{ marginTop: "1rem" }}>
            <GoabButton type='secondary' size='compact'  onClick={() => openAddModal()}>Add new+</GoabButton>
          </div>
          }

      </div>

      {/* ***************Modals *******************/}
      <GoabDrawer
        position="right"
        heading={modalState.mode === "add" ? "Add work performed entry" : "Edit work performed entry"}
        open={modalState.open}
        maxSize="20vw"
        onClose={closeModal}
        actions={
          <GoabButtonGroup alignment='end'>
            <GoabButton testId='ud-work-entry-modal-cancel' type='text' size='compact' onClick={closeModal}>Cancel</GoabButton>
            {modalState.mode === "add" && (
              <GoabButton testId='ud-work-entry-modal-add-another' type='secondary' size='compact' onClick={() => onModalSave(true)}>Add another</GoabButton>
            )}
            <GoabButton testId='ud-work-entry-modal-save' type='primary' size='compact' onClick={() => onModalSave(false)}>{modalState.mode === "add" ? "Add" : "Save"}</GoabButton>
          </GoabButtonGroup>
        }
      >
        <div className="work-modal-fields">
          <GoabFormItem label="Work performed" >
            <Autocomplete className='formField' includeInputInList filterSelectedOptions  autoComplete  sx={{ width: "100%" }} id={"workPerformed_opt"} data-testid={"tnc_workPerformed_opt"} noOptionsText="--type to select--"
              renderInput={(params) => <TextField {...params} placeholder="--type to select--" />}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option.label ?? "")}
              options={workPerformedOptions}
              filterOptions={filterOptionsGenListType}
              value={modalState.draft.values.workPerformed as GenListType | null}
              onChange={(_event, item: GenListType | null) => onWorkPerformedChange(item)}
              inputValue={workPerformedInputValue}
              onInputChange={(_event, newInputValue, reason) => onWorkPerformedInputChange(newInputValue, reason)}
            />
          </GoabFormItem>

          {showAssignedToWildfire && (
            <GoabFormItem label="Were you assigned to a wildfire?" error={modalState.draft.touched.assignedToWildfire === "T" ? modalState.draft.errors.assignedToWildfire as string : ""}>
              <GoabRadioGroup
                name="assignedToWildfire"
                value={modalState.draft.values.assignedToWildfire || ""}
                orientation="horizontal"
                onChange={(detail) => onMyChange({ assignedToWildfire: detail.value, ...(detail.value !== "yes" ? { fireNumber: null } : {}) })}
              >
                <GoabRadioItem value="yes" label="Yes" />
                <GoabRadioItem value="no" label="No" />
              </GoabRadioGroup>
            </GoabFormItem>
          )}

          {modalState.draft.values.assignedToWildfire === "yes" && (
            <GoabFormItem label="Fire number" error={modalState.draft.touched.fireNumber === "T" ? modalState.draft.errors.fireNumber as string : ""}>
              <Autocomplete
                className='formField'
                filterSelectedOptions
                autoComplete
                fullWidth
                noOptionsText="--type to select--"
                renderInput={(params) => <Tooltip placement='top' arrow title={(modalState.draft.values.fireNumber as GenListType)?.label ?? ""}><TextField {...params} placeholder="--type to select--" /></Tooltip>}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option.label ?? "")}
                options={fireNumberOptions}
                filterOptions={filterOptionsGenListType}
                value={modalState.draft.values.fireNumber as GenListType | null}
                onChange={(_event, item: GenListType | null) => onMyChange({ fireNumber: item })}
                onInputChange={(_event, newInputValue, reason) => { if (reason === "input") searchFireNumbers(newInputValue); }}
              />
            </GoabFormItem>
          )}

          {showFuelProvidedByGoa && (
            <GoabFormItem label="Was fuel provided by the GOA?" error={modalState.draft.touched.fuelProvidedByGoa === "T" ? modalState.draft.errors.fuelProvidedByGoa as string : ""}>
              <GoabRadioGroup
                name="fuelProvidedByGoa"
                value={modalState.draft.values.fuelProvidedByGoa || ""}
                orientation="horizontal"
                onChange={(detail) => onMyChange({ fuelProvidedByGoa: detail.value })}
              >
                <GoabRadioItem value="yes" label="Yes" />
                <GoabRadioItem value="no" label="No" />
              </GoabRadioGroup>
            </GoabFormItem>
          )}

          <div className="work-modal-time-row">
            <GoabFormItem label="Start time" error={modalState.draft.touched.startUtc === "T" ? modalState.draft.errors.startUtc as string : ""}>
              <TextField
                size="small"
                type="text"
                placeholder="HH:mm"
                defaultValue={formatTimeOnly(modalState.draft.values.startUtc as string)}
                key={`start-${modalState.draft.values.id}-${formatTimeOnly(modalState.draft.values.startUtc as string)}`}
                inputProps={{ maxLength: 5, inputMode: "numeric", pattern: "([01][0-9]|2[0-3]):[0-5][0-9]" }}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.ctrlKey || e.metaKey || e.altKey) return;
                  if (["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Enter", "Escape"].includes(e.key)) {
                    return;
                  }
                  if (!/^[0-9:]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[^0-9:]/g, "");
                  if (sanitized !== e.target.value) {
                    e.target.value = sanitized;
                  }
                }}
                onBlur={(e) => { const normalized = wdpUtil.normalizeTypedTimeInput(e.target.value); e.target.value = normalized; onModalTimeFieldBlur("startUtc", normalized); }}
                error={modalState.draft.touched.startUtc === "T" && modalState.draft.errors.startUtc !== ""}
                aria-label="Start time"
              />
            </GoabFormItem>

            <GoabFormItem label="Stop time" error={modalState.draft.touched.finishUtc === "T" ? modalState.draft.errors.finishUtc as string : ""}>
              <TextField
                size="small"
                type="text"
                placeholder="HH:mm"
                defaultValue={formatTimeOnly(modalState.draft.values.finishUtc as string)}
                key={`finish-${modalState.draft.values.id}-${formatTimeOnly(modalState.draft.values.finishUtc as string)}`}
                inputProps={{ maxLength: 5, inputMode: "numeric", pattern: "([01][0-9]|2[0-3]):[0-5][0-9]" }}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.ctrlKey || e.metaKey || e.altKey) return;
                  if (["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Enter", "Escape"].includes(e.key)) {
                    return;
                  }
                  if (!/^[0-9:]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[^0-9:]/g, "");
                  if (sanitized !== e.target.value) {
                    e.target.value = sanitized;
                  }
                }}
                onBlur={(e) => { const normalized = wdpUtil.normalizeTypedTimeInput(e.target.value); e.target.value = normalized; onModalTimeFieldBlur("finishUtc", normalized); }}
                error={modalState.draft.touched.finishUtc === "T" && modalState.draft.errors.finishUtc !== ""}
                aria-label="Stop time"
              />
            </GoabFormItem>
          </div>

          <GoabFormItem label="Total hours">
            <span className="total-hours">{modalState.draft.values.noOfUnits ? formatHoursAsDuration(modalState.draft.values.noOfUnits) : ""}</span>
          </GoabFormItem>
        </div>
      </GoabDrawer>

      <GoabModal heading={'Confirmation: Delete work performed entry'} calloutVariant='emergency' open={deleteModalState !== false} maxWidth='30vw'
        actions={
          <GoabButtonGroup alignment='end'>
            <GoabButton testId='ud-delete-work-entries-modal-no' type='secondary' size='compact' onClick={onCancelDeleteModal}>No, Cancel</GoabButton>
            <GoabButton testId='ud-delete-work-entries-modal-yes' type='primary' size='compact' onClick={onConfirmDeleteModal}>Yes, delete</GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>The row(s) associated with the deleted work performed entry will be deleted from the Cost Summary table.</p>
      </GoabModal>
    </section>
  );
}
